import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiPage } from "../../models/api-page.model";
import { Fee } from "../../models/fee.model";
import { FeeService } from "../../services/fee.service";
import { setPreviousSelectedOfficeId } from "./OfficeSlice";
import { logger } from "../../utils/logger";
import { ServiceOwner } from "../../models/service.model";

const log = logger.getLogger("FeeErrors");

interface ListFeesType {
  officeId?: string;
  searchTerm?: string;
  page: number;
  size: number;
  sortType: string;
  fullPageLoaderShown?: boolean;
  feeOwner?: ServiceOwner;
}

interface InitialStateType {
  loading: boolean;
  error: boolean;
  success: boolean;
  feeList: ApiPage<Fee> | null;
  archivedFees: ApiPage<Fee> | null;
  fullPageLoaderShown: boolean;
  sort: {
    start: number;
    end: number;
    pageNumber: number;
    rowsPerPage: number;
    sortedBy: string;
  };
}

const initialState: InitialStateType = {
  loading: false,
  error: false,
  success: false,
  feeList: null,
  archivedFees: null,
  fullPageLoaderShown: false,
  sort: {
    start: 0,
    end: 10,
    pageNumber: 0,
    rowsPerPage: 10,
    sortedBy: "name,asc",
  },
};

export const FeeSlice = createSlice({
  name: "fileApi",
  initialState,
  reducers: {
    resetStateFeeSlice: () => initialState,
    clearFeeList: state => {
      state.feeList = null;
      state.fullPageLoaderShown = false;
    },
    resetFeeTableSort: state => {
      state.sort = {
        start: 0,
        end: 10,
        pageNumber: 0,
        rowsPerPage: 10,
        sortedBy: "name,asc",
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(searchSystemFees.pending, state => {
        state.loading = false;
        state.error = false;
      })
      .addCase(searchSystemFees.rejected, state => {
        state.error = true;
        state.loading = false;
        state.fullPageLoaderShown = false;
      })
      .addCase(searchSystemFees.fulfilled, (state, action: PayloadAction<ApiPage<Fee>>) => {
        state.error = false;
        state.loading = false;
        state.fullPageLoaderShown = false;
        state.feeList = action.payload;
      })
      .addCase(searchOfficeFees.pending, state => {
        state.loading = false;
        state.error = false;
      })
      .addCase(searchOfficeFees.rejected, state => {
        state.error = true;
        state.loading = false;
        state.fullPageLoaderShown = false;
      })
      .addCase(searchOfficeFees.fulfilled, (state, action: PayloadAction<ApiPage<Fee>>) => {
        state.error = false;
        state.loading = false;
        state.fullPageLoaderShown = false;
        state.feeList = action.payload;
      })
      .addCase(addSystemFee.pending, state => {
        state.error = false;
      })
      .addCase(addSystemFee.rejected, state => {
        state.error = true;
        state.loading = false;
        state.fullPageLoaderShown = false;
      })
      .addCase(addSystemFee.fulfilled, (state, action: PayloadAction<Fee | null>) => {
        if (action.payload !== null) {
          state.error = false;
        }
        state.loading = false;
        state.fullPageLoaderShown = false;
      })
      .addCase(updateSystemFee.pending, state => {
        state.error = false;
      })
      .addCase(updateSystemFee.rejected, state => {
        state.error = true;
        state.loading = false;
        state.fullPageLoaderShown = false;
      })
      .addCase(updateSystemFee.fulfilled, (state, action: PayloadAction<Fee | null>) => {
        if (action.payload !== null) {
          state.error = false;
        }
        state.loading = false;
        state.fullPageLoaderShown = false;
      })
      .addCase(addOfficeFee.pending, state => {
        state.error = false;
      })
      .addCase(addOfficeFee.rejected, state => {
        state.error = true;
        state.loading = false;
        state.fullPageLoaderShown = false;
      })
      .addCase(addOfficeFee.fulfilled, (state, action: PayloadAction<Fee | null>) => {
        if (action.payload !== null) {
          state.error = false;
        }
        state.loading = false;
        state.fullPageLoaderShown = false;
      })
      .addCase(updateOfficeFee.pending, state => {
        state.error = false;
      })
      .addCase(updateOfficeFee.rejected, state => {
        state.error = true;
        state.loading = false;
        state.fullPageLoaderShown = false;
      })
      .addCase(updateOfficeFee.fulfilled, (state, action: PayloadAction<Fee | null>) => {
        if (action.payload !== null) {
          state.error = false;
        }
        state.loading = false;
        state.fullPageLoaderShown = false;
      })
      .addCase(setTableState.fulfilled, (state, action: PayloadAction<ListFeesType>) => {
        const { sortType, page, size } = action.payload;
        const obj = {
          pageNumber: page,
          rowsPerPage: size,
          sortedBy: sortType,
        };
        state.sort = { ...state.sort, ...obj };
      })
      .addCase(copyAllFeeToOffice.pending, state => {
        state.loading = true;
      })
      .addCase(copyAllFeeToOffice.rejected, state => {
        state.loading = false;
      })
      .addCase(copyAllFeeToOffice.fulfilled, state => {
        state.loading = false;
      })
      .addCase(getOfficeArchivedFees.pending, state => {
        state.loading = true;
      })
      .addCase(getOfficeArchivedFees.fulfilled, (state, action: PayloadAction<ApiPage<Fee>>) => {
        state.loading = false;
        state.archivedFees = action.payload;
      })
      .addCase(getOfficeArchivedFees.rejected, state => {
        state.loading = false;
      })
      .addCase(restoreOfficeFee.pending, state => {
        state.loading = true;
      })
      .addCase(restoreOfficeFee.fulfilled, (state, action: PayloadAction<Fee | null>) => {
        state.loading = false;
        if (action.payload && state.archivedFees) {
          // Remove restored fee from archived fees list
          state.archivedFees = {
            ...state.archivedFees,
            content: state.archivedFees.content.filter(fee => fee.feeId !== action.payload?.feeId),
            totalElements: state.archivedFees.totalElements - 1,
          };
        }
      })
      .addCase(restoreOfficeFee.rejected, state => {
        state.loading = false;
      });
  },
});

export const setTableState = createAsyncThunk("ServiceApi/sortData", async ({ page, sortType, size }: ListFeesType, { rejectWithValue }) => {
  try {
    return { sortType, page, size };
  } catch (e) {
    log.warn(JSON.stringify(e));
    return rejectWithValue(null);
  }
});

export const searchSystemFees = createAsyncThunk(
  "FeeSlice/searchSystemFees",
  async ({ page, sortType, size, searchTerm = "" }: ListFeesType, { rejectWithValue, dispatch }) => {
    try {
      const res = await FeeService.searchSystemFees(searchTerm, page, size, sortType);
      dispatch(setTableState({ sortType, page, size }));
      return res;
    } catch (e) {
      log.warn(JSON.stringify(e));
      return rejectWithValue(null);
    }
  }
);

export const searchOfficeFees = createAsyncThunk(
  "FeeSlice/searchOfficeFees",
  async ({ page, sortType, size, searchTerm = "", officeId, feeOwner }: ListFeesType, { rejectWithValue, dispatch }) => {
    try {
      if (officeId) {
        const res = await FeeService.searchOfficeFees(officeId, searchTerm, page, size, sortType, feeOwner);
        dispatch(setTableState({ sortType, page, size }));
        return res;
      } else {
        return rejectWithValue(null);
      }
    } catch (e) {
      log.warn(JSON.stringify(e));
      return rejectWithValue(null);
    }
  }
);

export const addSystemFee = createAsyncThunk("FeeSlice/addSystemFee", async (fee: Fee, { rejectWithValue }) => {
  try {
    const res = await FeeService.addSystemFee(fee);
    return res;
  } catch (e) {
    log.warn(JSON.stringify(e));
    return rejectWithValue(null);
  }
});
export const updateSystemFee = createAsyncThunk("FeeSlice/updateSystemFee", async (fee: Fee, { rejectWithValue }) => {
  try {
    const res = await FeeService.updateSystemFee(fee);
    return res;
  } catch (e) {
    log.warn(JSON.stringify(e));
    return rejectWithValue(null);
  }
});

export const addOfficeFee = createAsyncThunk(
  "FeeSlice/addOfficeFee",
  async ({ fee, officeId }: { fee: Fee; officeId: string }, { rejectWithValue }) => {
    try {
      const res = await FeeService.addOfficeFee(officeId, fee);
      return res;
    } catch (e) {
      log.warn(JSON.stringify(e));
      return rejectWithValue(null);
    }
  }
);

export const updateOfficeFee = createAsyncThunk(
  "FeeSlice/updateOfficeFee",
  async ({ fee, officeId }: { fee: Fee; officeId: string }, { rejectWithValue }) => {
    try {
      const res = await FeeService.updateOfficeFee(officeId, fee);
      return res;
    } catch (e) {
      log.warn(JSON.stringify(e));
      return rejectWithValue(null);
    }
  }
);

export const copyFeeToOffice = createAsyncThunk(
  "FeeSlice/copyFeeToOffice",
  async ({ officeId, feeId }: { officeId: string; feeId: string }, { rejectWithValue }) => {
    try {
      const res = await FeeService.duplicateFeeToOffice(officeId, feeId);
      return res;
    } catch (e) {
      log.warn(JSON.stringify(e));
      return rejectWithValue(e);
    }
  }
);

export const copyAllFeeToOffice = createAsyncThunk("FeeSlice/copyAllFeeToOffice", async ({ officeId }: { officeId: string }, { rejectWithValue }) => {
  try {
    const res = await FeeService.duplicateAllFeeToOffice(officeId);
    return res;
  } catch (e) {
    log.warn(JSON.stringify(e));
    return rejectWithValue(e);
  }
});

export const enableOfficeFee = createAsyncThunk(
  "FeeSlice/enableOfficeFee",
  async ({ fee, officeId }: { fee: Fee; officeId: string }, { rejectWithValue }) => {
    try {
      const res = await FeeService.updateOfficeFee(officeId, fee);
      return res;
    } catch (e) {
      log.warn(JSON.stringify(e));
      return rejectWithValue(null);
    }
  }
);

export const getOfficeArchivedFees = createAsyncThunk(
  "FeeSlice/getOfficeArchivedFees",
  async ({ officeId, page, size, sort }: { officeId: string; page?: number; size?: number; sort?: string }, { rejectWithValue }) => {
    try {
      const res = await FeeService.getOfficeArchivedFees(officeId, page, size, sort);
      return res;
    } catch (e: any) {
      log.warn(JSON.stringify(e));
      return rejectWithValue(e);
    }
  }
);

export const restoreOfficeFee = createAsyncThunk(
  "FeeSlice/restoreOfficeFee",
  async ({ officeId, fee, archived = false }: { officeId: string; fee: Fee; archived?: boolean }, { rejectWithValue }) => {
    try {
      const res = await FeeService.restoreOfficeFee(officeId, fee, archived);
      return res;
    } catch (e: any) {
      log.warn(JSON.stringify(e));
      return rejectWithValue(e);
    }
  }
);

// Action creators are generated for each case reducer function
export const { resetStateFeeSlice, resetFeeTableSort, clearFeeList } = FeeSlice.actions;

export default FeeSlice.reducer;
